import { useTheme } from "@mui/material";
import { Box } from "lucide-react";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import type { PaidAdvisoryDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { BasicInfo } from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/utils";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail.Services;

export const PaidAdvisory: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);

	if (isDocumentsLoading) {
		return <DetailSectionLoader />;
	}

	const portfolioData = baseData.data as PaidAdvisoryDetailDataDTO;

	const fixedPrice = portfolioData.fixedPriceByFrequency
		? getPortfolioFrequencyValue(
				portfolioData?.fixedPriceByFrequency ?? 0,
				currencySign,
				portfolioData?.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	return (
		<>
			<DataCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={<BasicInfo data={baseData} />}
			>
				<DataRow label={t(PageResources.name)} value={baseData.clientName} />
				<DataRow
					label={t(PageResources.contractNumber)}
					value={baseData?.contractNumber}
				/>
				<DataRow
					label={t(PageResources.producent)}
					value={baseData?.producer}
				/>
				<DataRow label={t(PageResources.product)} value={baseData?.product} />
			</DataCard>

			<DataCard
				label={t(PageResources.cardLabel)}
				icon={<Box color={theme.palette.deprecated.grey[700]} />}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
			>
				<DataRow
					label={t(Resources.PortfolioDetail.PaidAdvisory.fixedPrice)}
					value={fixedPrice}
				/>
			</DataCard>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</>
	);
};
